import { Helmet } from '@/components/base/SEOHead';
import { Link } from 'react-router-dom';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 福利视频大全',
  description: '福利视频大全的品牌介绍、发展历程、核心优势和联系方式。',
  url: 'https://fulivideo.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '福利视频大全',
    url: 'https://fulivideo.com',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '福利视频大全',
  url: 'https://fulivideo.com',
  description: '福利视频专题页，汇聚海量精选福利视频资源，涵盖各类热门内容，免费在线观看，持续更新不断。',
  email: 'contact@fulivideo.com',
  foundingDate: '2024',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@fulivideo.com',
    availableLanguage: ['Chinese'],
    hoursAvailable: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://fulivideo.com' },
    { '@type': 'ListItem', position: 2, name: '关于我们' },
  ],
};

const testimonials = [
  {
    name: '李明',
    role: '资深影评人',
    content: '福利视频大全是我每天必逛的网站，内容更新速度非常快，视频质量也很高。无论是热播剧的花絮还是综艺的搞笑片段，这里都能第一时间看到。强烈推荐给所有喜欢看视频的朋友！',
    rating: 5,
  },
  {
    name: '王芳',
    role: '自媒体博主',
    content: '作为一个内容创作者，我经常需要找一些灵感和素材。福利视频大全的分类做得非常好，找起视频来特别方便。而且完全免费，真的是良心网站！',
    rating: 5,
  },
  {
    name: '张伟',
    role: '体育爱好者',
    content: '我是通过朋友推荐开始使用福利视频大全的，从此就停不下来了。体育赛事集锦更新得特别及时，周末足球比赛结束当晚就能看到精彩进球集锦。网站体验也特别好！',
    rating: 5,
  },
  {
    name: '陈晓',
    role: '二次元粉丝',
    content: '作为动漫爱好者，福利视频大全的动漫分类简直就是宝藏！新番推荐、经典回顾应有尽有，而且都是精选过的高质量内容。每天都要上来看看有没有更新，已经成了我的日常习惯。',
    rating: 5,
  },
  {
    name: '刘洋',
    role: '音乐达人',
    content: '音乐分类的视频资源非常丰富，演唱会精华、音乐节目片段都很精彩。网站界面设计简洁大方，浏览体验很流畅。推荐给所有音乐爱好者！',
    rating: 5,
  },
  {
    name: '赵雪',
    role: '职场白领',
    content: '工作之余喜欢看看综艺节目放松，福利视频大全的综艺分类完美满足了我的需求。搞笑片段特别多，每次看都能笑得停不下来。已经推荐给好几个同事了！',
    rating: 5,
  },
];

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>关于我们 - 福利视频大全 | 品牌介绍与联系方式</title>
        <meta name="description" content="福利视频大全品牌介绍、发展历程、核心优势、用户口碑和联系方式。了解我们如何为用户提供最优质的免费福利视频资源。" />
        <meta name="keywords" content="关于我们,福利视频大全,品牌介绍,联系方式,用户口碑" />
        <link rel="canonical" href="https://fulivideo.com/about" />
        <meta property="og:title" content="关于我们 - 福利视频大全" />
        <meta property="og:description" content="了解福利视频大全的品牌故事、核心优势、用户口碑和联系方式。" />
        <meta property="og:url" content="https://fulivideo.com/about" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(aboutSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(orgSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main>
        <section className="py-10 md:py-14 bg-background-100">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-950 font-medium">关于我们</span>
            </nav>

            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 font-heading mb-4">
              关于福利视频大全
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
              福利视频大全是一个专注于精选视频资源聚合的在线平台，致力于为每一位用户提供高品质、多样化的免费视频观看体验。
            </p>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading mb-4">
                  我们的故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    福利视频大全成立于2024年，由一群热爱视频内容的互联网从业者共同创建。我们发现在互联网上海量的视频资源中，用户往往需要花费大量时间才能找到真正优质的内容。于是我们决定打造一个精选视频聚合平台，帮助用户高效地发现和享受优质视频内容。
                  </p>
                  <p>
                    从最初的几十个视频，到如今汇聚超过1000个精选视频资源，涵盖影视、综艺、音乐、体育、动漫、游戏、生活时尚等多个领域，福利视频大全始终坚持以用户为中心，不断优化内容质量和浏览体验。
                  </p>
                  <p>
                    我们的编辑团队每天精心筛选互联网上最新、最热门的视频内容，确保每一个上线的视频都是值得一看的精品。无论你是影视爱好者、综艺迷、音乐发烧友还是体育狂热粉，都能在这里找到属于你的精彩内容。
                  </p>
                </div>
              </div>

              <div className="relative aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20team%20working%20together%20in%20a%20bright%20open%20office%20with%20large%20monitors%20displaying%20video%20content%2C%20collaborative%20atmosphere%2C%20warm%20natural%20light%20through%20large%20windows%2C%20minimalist%20workspace%20design%2C%20casual%20and%20friendly%20vibe%2C%20professional%20yet%20relaxed%20environment&width=800&height=600&seq=about-story-2026&orientation=landscape"
                  alt="福利视频大全团队"
                  title="福利视频大全 - 我们的团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading mb-8 text-center">
              核心优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mx-auto mb-4">
                  <i className="ri-vip-diamond-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-2 font-heading">精选内容</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">专业编辑团队每天精心筛选，确保每一个视频都值得观看</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                  <i className="ri-flashlight-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-2 font-heading">极速更新</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">每日更新最新内容，热门视频第一时间上线，永不错过精彩</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mx-auto mb-4">
                  <i className="ri-layout-grid-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-2 font-heading">分类清晰</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">八大内容分类，快速找到感兴趣的视频类型，浏览效率翻倍</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mx-auto mb-4">
                  <i className="ri-emotion-happy-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-2 font-heading">完全免费</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">所有视频免费观看，无需注册登录，打开即看，零门槛使用</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading mb-8 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((item, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-5"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{item.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-semibold">
                      {item.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading mb-4">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                  如果你有任何问题、建议或合作意向，欢迎通过以下方式与我们联系。我们会尽快回复你的消息。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-950 mb-0.5">电子邮箱</h3>
                      <p className="text-sm text-foreground-600">contact@fulivideo.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-customer-service-2-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-950 mb-0.5">在线客服</h3>
                      <p className="text-sm text-foreground-600">工作日 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-950 mb-0.5">办公地址</h3>
                      <p className="text-sm text-foreground-600">北京市朝阳区科技园路88号创新大厦</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-3">
                  <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 hover:border-primary-200 cursor-pointer transition-colors" aria-label="微博" rel="nofollow">
                    <i className="ri-weibo-line text-lg"></i>
                  </a>
                  <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 hover:border-primary-200 cursor-pointer transition-colors" aria-label="微信" rel="nofollow">
                    <i className="ri-wechat-2-line text-lg"></i>
                  </a>
                  <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 hover:border-primary-200 cursor-pointer transition-colors" aria-label="哔哩哔哩" rel="nofollow">
                    <i className="ri-bilibili-line text-lg"></i>
                  </a>
                </div>
              </div>

              <div className="relative aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20office%20building%20exterior%20in%20Beijing%20city%20with%20glass%20facade%2C%20clean%20architectural%20design%2C%20blue%20sky%20with%20light%20clouds%2C%20professional%20corporate%20headquarters%2C%20tree-lined%20street%20in%20front%2C%20warm%20sunlight%2C%20minimalist%20urban%20scene&width=800&height=600&seq=about-contact-2026&orientation=landscape"
                  alt="福利视频大全办公地址"
                  title="福利视频大全 - 办公地点"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-accent-50">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6 text-center">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading mb-3">
              准备开始探索精彩内容了吗？
            </h2>
            <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
              上千个精选福利视频等你发现，立即浏览，找到你喜欢的精彩内容
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/all"
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line mr-1.5"></i>
                浏览全部视频
              </Link>
              <Link
                to="/category/hot"
                className="px-6 py-2.5 bg-background-50 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium hover:border-primary-300 hover:text-primary-500 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-fire-line mr-1.5"></i>
                热门推荐
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}